import { apiClient } from './api';
import { API_BASE_URL, TOKEN_STORAGE_KEY } from '@/utils/constants';

// VERACODE-MITIGATION: CWE-918 - All API requests use the environment-configured base URL
// (VITE_API_BASE_URL) via the shared API client. URLs are not user-controlled.

interface ApiMeta {
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

export interface ThreatIndicator {
  type: string;
  value: string;
}

export interface ThreatAssessment {
  id: string;
  title: string;
  description: string;
  threatLevel: 'low' | 'medium' | 'high' | 'critical';
  confidence: 'low' | 'medium' | 'high';
  category: 'cyber' | 'physical' | 'personnel' | 'reputation' | 'financial';
  sources: string[];
  targetedAssets: string[];
  indicators: (string | ThreatIndicator)[];
  recommendations: string[];
  createdAt: string;
  updatedAt: string;
  analyst: string;
  status: 'draft' | 'review' | 'published' | 'archived';
}

export interface ThreatSource {
  id: string;
  name: string;
  type: 'osint' | 'commercial' | 'government' | 'internal';
  reliability: 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
  url?: string;
  lastUpdate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface ThreatStats {
  total: number;
  active: number;
  monitored: number;
  critical: number;
  bySeverity?: Record<string, number>;
  byType?: Record<string, number>;
  byStatus?: Record<string, number>;
  recentTrends?: {
    last24h: number;
    last7d: number;
    last30d: number;
  };
}

export interface ThreatReport {
  id: string;
  reportNumber: string;
  title: string;
  format: 'pdf' | 'excel' | 'csv' | 'html';
  fileSize: number;
  pageCount?: number;
  totalThreats: number;
  criticalThreats: number;
  highThreats: number;
  mediumThreats: number;
  lowThreats: number;
  generatedAt: string;
  status: string;
  generatedBy: string;
  generatedByName: string;
  generatedByEmail: string;
  startDate: string;
  endDate: string;
  threatLevels?: string[];
  categories?: string[];
  statuses?: string[];
}

// Feed Preference for pre-configured RSS feeds (per-user)
export interface FeedPreference {
  id: string;
  sourceId: string;
  sourceName: string;
  sourceUrl: string;
  sourceType: string;
  sourceReliability: string;
  category: 'regional' | 'national_security' | 'breaking_news';
  categoryDisplayName: string;
  description: string;
  enabled: boolean;
  lastSyncedAt: string | null;
  feedTitle: string | null;
  itemCount?: number;
}

// RSS Feed Item from ThreatFeedItem table
export interface ThreatFeedItem {
  id: string;
  sourceId: string;
  sourceName: string;
  sourceType: string;
  sourceReliability: string;
  sourceCategory: string;
  externalId?: string;
  title: string;
  description?: string;
  link?: string;
  publishedDate: string;
  threatLevel?: 'low' | 'medium' | 'high' | 'critical';
  category?: string;
  indicators?: string[];
  affectedAssets?: string[];
  tags?: string[];
  processed: boolean;
  processedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Feed Items Statistics
export interface FeedItemStats {
  total: number;
  processed: number;
  unprocessed: number;
  byThreatLevel: Record<string, number>;
  byCategory: Record<string, number>;
  bySource: Record<string, number>;
  last24Hours: number;
  last7Days: number;
}

// Paginated Response for feed items
export interface PaginatedFeedItems {
  data: ThreatFeedItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

class ThreatIntelligenceService {
  /**
   * Get all threat assessments
   */
  async getThreatAssessments(): Promise<ThreatAssessment[]> {
    const response = await apiClient.get<ThreatAssessment[]>('/api/v1/threats');
    return response.success && response.data ? response.data : [];
  }

  /**
   * Get threat assessment by ID
   */
  async getThreatAssessmentById(id: string): Promise<ThreatAssessment | null> {
    const response = await apiClient.get<ThreatAssessment>(`/api/v1/threats/${id}`);
    return response.success && response.data ? response.data : null;
  }

  /**
   * Create threat assessment
   */
  async createThreatAssessment(data: Omit<ThreatAssessment, 'id' | 'createdAt' | 'updatedAt'>): Promise<ThreatAssessment | null> {
    const response = await apiClient.post<ThreatAssessment>('/api/v1/threats', data);
    return response.success && response.data ? response.data : null;
  }

  /**
   * Update threat assessment
   */
  async updateThreatAssessment(id: string, data: Partial<ThreatAssessment>): Promise<ThreatAssessment | null> {
    const response = await apiClient.put<ThreatAssessment>(`/api/v1/threats/${id}`, data);
    return response.success && response.data ? response.data : null;
  }

  /**
   * Delete threat assessment
   */
  async deleteThreatAssessment(id: string): Promise<boolean> {
    const response = await apiClient.delete(`/api/v1/threats/${id}`);
    return response.success;
  }

  /**
   * Get all threat intelligence sources
   */
  async getThreatSources(): Promise<ThreatSource[]> {
    const response = await apiClient.get<ThreatSource[]>('/api/v1/threats/sources');
    return response.success && response.data ? response.data : [];
  }

  /**
   * Get threat source by ID
   */
  async getThreatSourceById(id: string): Promise<ThreatSource | null> {
    const response = await apiClient.get<ThreatSource>(`/api/v1/threats/sources/${id}`);
    return response.success && response.data ? response.data : null;
  }

  /**
   * Create threat intelligence source (including RSS feeds)
   */
  async createThreatSource(data: Omit<ThreatSource, 'id' | 'createdAt' | 'lastUpdate'>): Promise<ThreatSource | null> {
    const response = await apiClient.post<ThreatSource>('/api/v1/threats/sources', data);
    return response.success && response.data ? response.data : null;
  }

  /**
   * Update threat intelligence source
   */
  async updateThreatSource(id: string, data: Partial<ThreatSource>): Promise<ThreatSource | null> {
    const response = await apiClient.put<ThreatSource>(`/api/v1/threats/sources/${id}`, data);
    return response.success && response.data ? response.data : null;
  }

  /**
   * Delete threat intelligence source
   */
  async deleteThreatSource(id: string): Promise<boolean> {
    const response = await apiClient.delete(`/api/v1/threats/sources/${id}`);
    return response.success;
  }

  /**
   * Test RSS feed connectivity and fetch metadata
   */
  async testRssFeed(id: string): Promise<{
    feedTitle: string;
    feedDescription: string;
    itemCount: number;
    lastBuildDate: string | null;
    link: string;
  } | null> {
    const response = await apiClient.post<{
      feedTitle: string;
      feedDescription: string;
      itemCount: number;
      lastBuildDate: string | null;
      link: string;
    }>(`/api/v1/threats/sources/${id}/test-feed`, {});
    return response.success && response.data ? response.data : null;
  }

  /**
   * Sync threats from RSS feed
   */
  async syncRssFeed(id: string): Promise<{
    threatsCreated: number;
    totalItems: number;
    feedTitle: string;
  } | null> {
    const response = await apiClient.post<{
      threatsCreated: number;
      totalItems: number;
      feedTitle: string;
    }>(`/api/v1/threats/sources/${id}/sync-feed`, {});
    return response.success && response.data ? response.data : null;
  }

  /**
   * Get threat intelligence statistics
   */
  async getThreatStats(): Promise<ThreatStats | null> {
    const response = await apiClient.get<ThreatStats>('/api/v1/threats/stats');
    return response.success && response.data ? response.data : null;
  }

  /**
   * Generate a new threat intelligence report
   */
  async generateReport(
    filters: {
      startDate: string;
      endDate: string;
      threatLevels?: string[];
      categories?: string[];
      statuses?: string[];
    },
    format: 'pdf' | 'excel' | 'csv' | 'html',
    title?: string
  ): Promise<ThreatReport | null> {
    const response = await apiClient.post<ThreatReport>('/api/v1/threats/reports', {
      filters,
      format,
      title,
    });
    return response.success && response.data ? response.data : null;
  }

  /**
   * Get report metadata by ID
   */
  async getReport(reportId: string): Promise<ThreatReport | null> {
    const response = await apiClient.get<ThreatReport>(`/api/v1/threats/reports/${reportId}`);
    return response.success && response.data ? response.data : null;
  }

  /**
   * Get HTML preview of report
   */
  async getReportPreview(reportId: string): Promise<string | null> {
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!token) {
        console.error('No authentication token found');
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/threats/reports/${reportId}/preview`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error(`Preview request failed with status ${response.status}`);
        // Throw error with status code for proper handling
        const error = new Error(`Failed to load preview (HTTP ${response.status})`) as Error & { status: number; response: { status: number } };
        error.status = response.status;
        error.response = { status: response.status };
        throw error;
      }

      return await response.text();
    } catch (error: unknown) {
      console.error('Failed to get report preview:', error);
      // Re-throw to allow caller to handle specific errors (e.g., 429)
      throw error;
    }
  }

  /**
   * Download report file
   */
  async downloadReport(reportId: string, filename: string): Promise<boolean> {
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!token) {
        console.error('No authentication token found');
        return false;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/threats/reports/${reportId}/download`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error(`Download request failed with status ${response.status}`);
        return false;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Failed to download report:', error);
      return false;
    }
  }

  /**
   * List user's generated reports
   */
  async listReports(limit: number = 20): Promise<ThreatReport[]> {
    const response = await apiClient.get<ThreatReport[]>(`/api/v1/threats/reports?limit=${limit}`);
    return response.success && response.data ? response.data : [];
  }

  /**
   * Delete report
   */
  async deleteReport(reportId: string): Promise<boolean> {
    const response = await apiClient.delete(`/api/v1/threats/reports/${reportId}`);
    return response.success;
  }

  // ==========================================
  // Feed Preferences API (per-user settings)
  // ==========================================

  /**
   * Get all feed preferences for the current user
   */
  async getFeedPreferences(): Promise<FeedPreference[]> {
    const response = await apiClient.get<FeedPreference[]>('/api/v1/threats/preferences/feeds');
    return response.success && response.data ? response.data : [];
  }

  /**
   * Get feed preferences grouped by category
   */
  async getFeedPreferencesGrouped(): Promise<Record<string, FeedPreference[]>> {
    const response = await apiClient.get<Record<string, FeedPreference[]>>('/api/v1/threats/preferences/feeds/grouped');
    return response.success && response.data ? response.data : {};
  }

  /**
   * Toggle a feed's enabled/disabled status
   */
  async toggleFeedPreference(sourceId: string, enabled: boolean): Promise<FeedPreference | null> {
    const response = await apiClient.put<FeedPreference>(`/api/v1/threats/preferences/feeds/${sourceId}`, { enabled });
    return response.success && response.data ? response.data : null;
  }

  /**
   * Bulk update feed preferences
   */
  async bulkUpdateFeedPreferences(preferences: Array<{ sourceId: string; enabled: boolean }>): Promise<{ updated: number }> {
    const response = await apiClient.put<{ updated: number }>('/api/v1/threats/preferences/feeds/bulk', { preferences });
    return response.success && response.data ? response.data : { updated: 0 };
  }

  /**
   * Initialize user preferences (set all feeds to enabled)
   */
  async initializeFeedPreferences(): Promise<{ created: number }> {
    const response = await apiClient.post<{ created: number }>('/api/v1/threats/preferences/feeds/initialize', {});
    return response.success && response.data ? response.data : { created: 0 };
  }

  // ==========================================
  // Feed Items API (RSS imported threats)
  // ==========================================

  /**
   * Get feed items with pagination and filtering
   */
  async getFeedItems(params?: {
    page?: number;
    limit?: number;
    threatLevel?: string;
    category?: string;
    sourceId?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedFeedItems> {
    const queryParams = new URLSearchParams();
    if (params?.page) { queryParams.append('page', params.page.toString()); }
    if (params?.limit) { queryParams.append('limit', params.limit.toString()); }
    if (params?.threatLevel) { queryParams.append('threatLevel', params.threatLevel); }
    if (params?.category) { queryParams.append('category', params.category); }
    if (params?.sourceId) { queryParams.append('sourceId', params.sourceId); }
    if (params?.search) { queryParams.append('search', params.search); }
    if (params?.sortBy) { queryParams.append('sortBy', params.sortBy); }
    if (params?.sortOrder) { queryParams.append('sortOrder', params.sortOrder); }

    const queryString = queryParams.toString();
    const url = `/api/v1/threats/feed-items${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<ThreatFeedItem[]>(url);

    // Handle the response which includes meta information
    if (response.success && response.data) {
      const meta = response.meta as ApiMeta;
      return {
        data: response.data,
        total: meta?.total ?? response.data.length,
        page: meta?.page ?? 1,
        limit: meta?.limit ?? 20,
        totalPages: meta?.totalPages ?? 1,
      };
    }

    return { data: [], total: 0, page: 1, limit: 20, totalPages: 0 };
  }

  /**
   * Get a single feed item by ID
   */
  async getFeedItemById(id: string): Promise<ThreatFeedItem | null> {
    const response = await apiClient.get<ThreatFeedItem>(`/api/v1/threats/feed-items/${id}`);
    return response.success && response.data ? response.data : null;
  }

  /**
   * Get feed item statistics
   */
  async getFeedItemStats(): Promise<FeedItemStats | null> {
    const response = await apiClient.get<FeedItemStats>('/api/v1/threats/feed-items/stats');
    return response.success && response.data ? response.data : null;
  }

  /**
   * Search feed items
   */
  async searchFeedItems(query: string, limit?: number): Promise<ThreatFeedItem[]> {
    const params = new URLSearchParams({ q: query });
    if (limit) { params.append('limit', limit.toString()); }

    const response = await apiClient.get<ThreatFeedItem[]>(`/api/v1/threats/feed-items/search?${params.toString()}`);
    return response.success && response.data ? response.data : [];
  }
}

export const threatIntelligenceService = new ThreatIntelligenceService();
export default threatIntelligenceService;
